import type { JSX } from 'react';
import { ActionIcon, ColorSwatch, Group, Popover, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

/**
 * Colour for a bar. A small set of presets plus the browser's own colour
 * field, so the common case is one click and the unusual case is still
 * possible.
 */
const PRESETS = [
  '#7B2D5E', // CI Pflaume
  '#B0819E', // CI Pflaume 60
  '#C08A3E', // CI Ocker
  '#7E8C6E', // CI Salbei
  '#6E686B', // CI Steingrau
  '#2B2629', // CI Anthrazit
  // Two outside the CI, for plans that need more distinct colours.
  '#3B6EA5', // blue
  '#4E8A5A', // green
];

export function ColorPicker({
  value,
  onChange,
  inherited,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  /** Colour actually used when none is set, for the "inherited" swatch. */
  inherited?: string | null;
}): JSX.Element {
  const [opened, { close, toggle }] = useDisclosure();

  return (
    <Popover opened={opened} onClose={close} position="bottom-start" withArrow shadow="md">
      <Popover.Target>
        <ActionIcon
          variant="subtle"
          size="xs"
          onClick={toggle}
          title="Farbe wählen"
          aria-label="Farbe wählen"
        >
          <ColorSwatch
            size={12}
            color={value ?? inherited ?? 'var(--mantine-color-brand-6)'}
            withShadow={false}
          />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Stack gap="xs">
          <Group gap={6}>
            {PRESETS.map((preset) => (
              <ColorSwatch
                key={preset}
                color={preset}
                size={20}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  onChange(preset);
                  close();
                }}
              />
            ))}
          </Group>
          <Group gap="xs">
            <input
              type="color"
              value={value ?? inherited ?? '#7B2D5E'}
              onChange={(event) => onChange(event.currentTarget.value)}
              style={{
                width: 32,
                height: 24,
                padding: 0,
                border: '1px solid var(--mantine-color-gray-4)',
                borderRadius: 4,
                background: 'none',
              }}
              title="Eigene Farbe"
            />
            <Text
              size="xs"
              c="dimmed"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                onChange(null);
                close();
              }}
            >
              zurücksetzen
            </Text>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
